import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { firstValueFrom, Subject } from 'rxjs';
import { SessionErrorCode, SessionTokens } from 'src/app/utils/models/session.model';
import { GetUserToken } from 'src/app/utils/models/token.interface';
import { SessionApiService } from './session-api.service';

export const SS_KEY = 'TheToken';
const REFRESH_KEY = 'nes_refresh_token';
// Un token per ogni username: il "dispositivo ricordato" è legato all'utente che l'ha scelto
const DEVICE_TOKENS_KEY = 'nes_device_tokens';
const REFRESH_LOCK = 'nes-auth-refresh';
// Si rinnova l'access token con questo anticipo rispetto alla scadenza
const REFRESH_MARGIN_MS = 2 * 60 * 1000;
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * ok: sessione rinnovata (o già rinnovata da un'altra scheda).
 * expired: il refresh token non è più valido, serve un nuovo login.
 * error: problema temporaneo (rete, server): la sessione non va chiusa.
 */
export type RefreshOutcome = 'ok' | 'expired' | 'error';

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  utente?: GetUserToken;

  /** Emette quando la sessione non è più recuperabile (refresh fallito o logout da un'altra scheda). */
  readonly sessionExpired$ = new Subject<void>();

  private refreshTimer?: ReturnType<typeof setTimeout>;
  private refreshing?: Promise<RefreshOutcome>;

  constructor(
    private jwt: JwtHelperService,
    private sessionApi: SessionApiService
  ) {
    if (this.getToken()) {
      this.storeUser(this.getToken()!);
    }
    // Sincronizza le schede: se un'altra scheda rinnova la sessione o fa il logout, si allinea
    window.addEventListener('storage', event => {
      if (event.key !== null && event.key !== SS_KEY) return;
      const token = this.getToken();
      if (token) {
        this.utente = this.jwt.decodeToken(token) ?? undefined;
        this.scheduleRefresh();
      } else if (this.utente) {
        this.clearRefreshTimer();
        this.utente = undefined;
        this.sessionExpired$.next();
      }
    });
  }

  isLogged() {
    return this.getToken() !== null && this.utente;
  }

  getToken() {
    return localStorage.getItem(SS_KEY);
  }

  getRefreshToken() {
    return localStorage.getItem(REFRESH_KEY);
  }

  /** True se la sessione si può rinnovare senza nuovo login (client che ha ricevuto un refresh token). */
  canRefresh() {
    return !!this.getRefreshToken();
  }

  /** Salva l'access token (e, se c'è, il refresh token) di un login o di un rinnovo. */
  storeSession(tokens: SessionTokens) {
    if (tokens.refresh_token) {
      localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
    }
    this.storeUser(tokens.access_token);
  }

  /** Salva solo l'access token: es. quello rilasciato dopo la modifica del proprio profilo. */
  storeUser(token: string) {
    localStorage.setItem(SS_KEY, token);
    const jwtDecoded = this.jwt.decodeToken(token);
    this.utente = jwtDecoded;
    this.scheduleRefresh();
  }

  // ───────────── Dispositivo ricordato ─────────────

  getDeviceToken(username: string): string | undefined {
    return this.readDeviceTokens()[username.toLowerCase()];
  }

  setDeviceToken(username: string, token: string) {
    const tokens = this.readDeviceTokens();
    tokens[username.toLowerCase()] = token;
    localStorage.setItem(DEVICE_TOKENS_KEY, JSON.stringify(tokens));
  }

  private readDeviceTokens(): Record<string, string> {
    try {
      return JSON.parse(localStorage.getItem(DEVICE_TOKENS_KEY) ?? '{}');
    } catch {
      return {};
    }
  }

  // ───────────── Rinnovo della sessione ─────────────

  /**
   * Rinnova la sessione con il refresh token. Più chiamate contemporanee (stessa scheda) condividono
   * un'unica richiesta; fra schede diverse una sola alla volta (Web Locks) e chi arriva dopo
   * riusa il token già rinnovato.
   *
   * @param staleAccessToken l'access token con cui è fallita la richiesta che ha fatto partire il
   * rinnovo: se nel frattempo è cambiato, un'altra scheda ha già rinnovato e non serve rifarlo.
   */
  refreshSession(staleAccessToken: string | null = this.getToken()): Promise<RefreshOutcome> {
    if (!this.refreshing) {
      this.refreshing = this.exclusively(() => this.doRefresh(staleAccessToken)).finally(() => {
        this.refreshing = undefined;
      });
    }
    return this.refreshing;
  }

  private exclusively<T>(fn: () => Promise<T>): Promise<T> {
    const locks = (navigator as any).locks;
    return locks?.request ? locks.request(REFRESH_LOCK, fn) : fn();
  }

  private async doRefresh(staleAccessToken: string | null): Promise<RefreshOutcome> {
    // Un'altra scheda può aver già rinnovato mentre aspettavamo il lock
    if (staleAccessToken && this.getToken() && this.getToken() !== staleAccessToken) {
      this.storeUser(this.getToken()!);
      return 'ok';
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return 'expired';

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const tokens = await firstValueFrom(this.sessionApi.refresh(refreshToken));
        this.storeSession(tokens);
        return 'ok';
      } catch (e) {
        const error = e as HttpErrorResponse;
        if (error.status === 409 && error.error?.code === SessionErrorCode.REFRESH_IN_PROGRESS) {
          // Un'altra richiesta ha appena ruotato lo stesso token: se ha salvato il nuovo, lo si usa
          await sleep(400);
          const currentToken = this.getToken();
          if (this.getRefreshToken() !== refreshToken && currentToken) {
            this.storeUser(currentToken);
            return 'ok';
          }
          continue;
        }
        if (error.status === 401 || error.status === 403) return 'expired';
        return 'error';
      }
    }
    return 'error';
  }

  /** Programma il rinnovo prima che l'access token scada (utile anche per le chiamate fuori da HttpClient). */
  private scheduleRefresh() {
    this.clearRefreshTimer();
    const token = this.getToken();
    if (!token || !this.canRefresh()) return;

    const expiration = this.jwt.getTokenExpirationDate(token);
    if (!expiration) return;

    const delay = Math.min(
      Math.max(expiration.getTime() - Date.now() - REFRESH_MARGIN_MS, 0),
      MAX_TIMEOUT_MS
    );
    this.refreshTimer = setTimeout(async () => {
      // se il token è nel frattempo cambiato (altra scheda) doRefresh lo rileva e non fa nulla
      const outcome = await this.refreshSession(token);
      if (outcome === 'expired') {
        this.sessionExpired$.next();
      } else if (outcome === 'error') {
        // problema temporaneo: si riprova fra poco, finché l'access token non scade del tutto
        this.refreshTimer = setTimeout(() => this.scheduleRefresh(), 30 * 1000);
      }
    }, delay);
  }

  private clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  // ───────────── Logout ─────────────

  logout() {
    // Chiude la sessione anche sul server (best effort: se fallisce, scade da sola)
    const token = this.getToken();
    if (token && this.canRefresh()) {
      this.sessionApi.logout(token).subscribe({ error: () => {} });
    }
    this.clearRefreshTimer();

    // I dispositivi ricordati sopravvivono al logout: servono proprio a saltare la MFA al login dopo
    const deviceTokens = localStorage.getItem(DEVICE_TOKENS_KEY);
    sessionStorage.clear();
    localStorage.clear();
    if (deviceTokens) {
      localStorage.setItem(DEVICE_TOKENS_KEY, deviceTokens);
    }
    this.utente = undefined;
  }
}
